import { SearchIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import { Avatar } from '@components/Avatar'
import { EmptyNote } from '@components/EmptyState'
import Modal from '@components/Modal'
import { OptionRow } from '@components/OptionRow'
import { useAccommodations, useEvents, useExpenses, useFlights, useIdeas, usePayUps, useTripMembers } from '@hooks'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ChatCardIcon } from './ChatCard'
import {
	PickerBody,
	RowDetail,
	RowFacts,
	RowPill,
	RowText,
	RowTitle,
	Rows,
	SearchGlyph,
	SearchInput,
	SearchRow,
	TabButton,
	Tabs,
} from './ChatTagPicker.styles'
import {
	PICKER_TABS,
	filterPickerItems,
	pickerItems,
	presentCard,
	sheetTitle,
	tabTitle,
	type ChatCard,
	type ChatTagPickerItem,
	type ChatTagPickerTab,
} from './core'

interface ChatTagPickerProps {
	tripId: string
	myUserId: number
	/** The tab to open on, or null while closed. */
	openOn: ChatTagPickerTab | null
	/** What the Message already tags, which the picker leaves out. */
	tagged: readonly ChatCard[]
	onPick: (card: ChatCard) => void
	onClose: () => void
}

/** One row: a member's avatar and full name; an idea's category and who added it; anything else its key facts. */
const PickerRow: React.FC<{ item: ChatTagPickerItem; onPick: (card: ChatCard) => void }> = ({ item, onPick }) => {
	const { card } = item
	const presentation = presentCard(card)
	const person = card.kind === 'Member' ? card.people[0] : undefined
	const [firstLeg] = presentation.legs
	const legs = firstLeg
		? presentation.legs.length > 1
			? `${firstLeg.times ?? firstLeg.route} · ${presentation.legs.length} flights`
			: (firstLeg.times ?? firstLeg.route)
		: null
	const detail = item.byline ?? presentation.detail ?? legs
	return (
		<OptionRow type="button" onClick={() => onPick(card)}>
			{person ? (
				<Avatar user={person} size="lg" />
			) : (
				<ChatCardIcon glyph={presentation.glyph} category={presentation.category} thumbnail={card.thumbnail} size={40} />
			)}
			<RowText>
				<RowTitle>{person ? `${person.firstName} ${person.lastName}` : presentation.title}</RowTitle>
				{(presentation.pill || detail) && (
					<RowFacts>
						{presentation.pill && <RowPill>{presentation.pill}</RowPill>}
						{detail && <RowDetail>{detail}</RowDetail>}
					</RowFacts>
				)}
			</RowText>
		</OptionRow>
	)
}

interface PickerContentProps extends Omit<ChatTagPickerProps, 'openOn' | 'onClose'> {
	tab: ChatTagPickerTab
	onTabChange: (tab: ChatTagPickerTab) => void
}

const PickerContent: React.FC<PickerContentProps> = ({ tripId, myUserId, tab, onTabChange, tagged, onPick }) => {
	const [query, setQuery] = useState('')
	const searchRef = useRef<HTMLInputElement>(null)
	// What the webapp already holds, caught up if stale: there is no search endpoint.
	const members = useTripMembers(tripId).data?.members
	const flightGroups = useFlights(tripId).data?.flightGroups
	const accommodations = useAccommodations(tripId).data?.accommodations
	const events = useEvents(tripId).data?.events
	const ideas = useIdeas(tripId).data?.ideas
	const expenses = useExpenses(tripId).data?.expenses
	const payUps = usePayUps(tripId).data?.payUps

	useEffect(() => searchRef.current?.focus(), [])

	const items = useMemo(
		() =>
			pickerItems(
				{
					members: members ?? [],
					flightGroups: flightGroups ?? [],
					accommodations: accommodations ?? [],
					events: events ?? [],
					ideas: ideas ?? [],
					expenses: expenses ?? [],
					payUps: payUps ?? [],
				},
				myUserId,
			),
		[members, flightGroups, accommodations, events, ideas, expenses, payUps, myUserId],
	)
	const shown = filterPickerItems(items, tab, query, tagged)

	return (
		<PickerBody>
			<Tabs role="group" aria-label="What to attach">
				{PICKER_TABS.map(option => (
					<TabButton
						key={option}
						type="button"
						$selected={option === tab}
						aria-pressed={option === tab}
						onClick={() => onTabChange(option)}
					>
						{tabTitle(option)}
					</TabButton>
				))}
			</Tabs>
			<SearchRow>
				<SearchGlyph>
					<SearchIcon sx={{ fontSize: iconSizes.sm }} />
				</SearchGlyph>
				<SearchInput
					ref={searchRef}
					type="search"
					placeholder="Search"
					aria-label="Search"
					value={query}
					onChange={e => setQuery(e.target.value)}
					onKeyDown={e => {
						if (e.key === 'Enter' && shown[0]) {
							e.preventDefault()
							onPick(shown[0].card)
						}
					}}
				/>
			</SearchRow>
			<Rows>
				{shown.length === 0 ? (
					<EmptyNote>{query.trim() === '' ? 'Nothing to attach yet' : 'No matches'}</EmptyNote>
				) : (
					shown.map(item => <PickerRow key={`${item.card.kind}-${item.card.id}`} item={item} onPick={onPick} />)
				)}
			</Rows>
		</PickerBody>
	)
}

/**
 * The picker a chip or a typed `@` opens: type tabs over the trip's taggable things,
 * filtered in the browser as the search is typed. Picking a row closes it. Ported from
 * iOS `ChatTagPickerVC`; its trip data loads only while it's open.
 */
const ChatTagPicker: React.FC<ChatTagPickerProps> = ({ openOn, onClose, ...props }) => {
	// The tab picked since opening, or the one it opened on.
	const [picked, setPicked] = useState<{ openOn: ChatTagPickerTab | null; tab: ChatTagPickerTab }>({
		openOn: null,
		tab: 'all',
	})
	const tab = openOn !== null && picked.openOn === openOn ? picked.tab : (openOn ?? 'all')
	// Each opening starts on the tab it was opened on.
	useEffect(() => {
		if (openOn === null) setPicked({ openOn: null, tab: 'all' })
	}, [openOn])

	return (
		<Modal isOpen={openOn !== null} onClose={onClose} title={sheetTitle(tab)} size="medium">
			{openOn && (
				<PickerContent key={openOn} {...props} tab={tab} onTabChange={next => setPicked({ openOn, tab: next })} />
			)}
		</Modal>
	)
}

export default ChatTagPicker
